<script setup lang="ts">
    import { usePreferredReducedMotion } from '@vueuse/core';
    import { gsap } from 'gsap';

    type BackgroundColors = {
        baseStart: string;
        baseEnd: string;
        mainShapeStart: string;
        mainShapeEnd: string;
        highlight: string;
        backRightStart: string;
        backRightMid: string;
        backRightEnd: string;
        farRightStart: string;
        farRightEnd: string;
    };

    const backgroundViewBox = '0 0 3940 2160';

    const backgroundColors: BackgroundColors = {
        baseStart: '#D2D7DF',
        baseEnd: '#FFFFFF',
        mainShapeStart: '#004CC2',
        mainShapeEnd: '#00245C',
        highlight: '#FFFFFF',
        backRightStart: '#181005',
        backRightMid: '#3E2808',
        backRightEnd: '#D0B24D',
        farRightStart: '#D0B24D',
        farRightEnd: '#F3E3F0'
    };

    const circles = {
        main: {
            cx: 1400,
            cy: 1800,
            rx: 1500,
            ry: 1500
        },
        backRight: {
            cx: 3000,
            cy: 1250,
            rx: 1000,
            ry: 1000
        },
        farRight: {
            cx: 3700,
            cy: 900,
            rx: 850,
            ry: 850
        },
        backLeft: {
            cx: 1200,
            cy: -150,
            rx: 1600,
            ry: 1600
        }
    };

    const mobileCircles = {
        main: {
            cx: 1250,
            cy: 1850,
            rx: 1800,
            ry: 1800
        },
        backRight: {
            cx: 3150,
            cy: 1100,
            rx: 1200,
            ry: 1200
        },
        farRight: {
            cx: 3800,
            cy: 520,
            rx: 950,
            ry: 950
        },
        backLeft: {
            cx: 850,
            cy: -250,
            rx: 1750,
            ry: 1750
        }
    };

    const parallaxLayers = [
        { key: 'backLeft', damp: 0.03 },
        { key: 'backRight', damp: 0.05 },
        { key: 'farRight', damp: 0.04 },
        { key: 'main', damp: 0.07 }
    ] as const;

    const parallaxGroup = ref(null);
    const circleMain = ref(null);
    const circleBackLeft = ref(null);
    const circleBackRight = ref(null);
    const circleFarRight = ref(null);

    const mm = gsap.matchMedia();
    const preferredMotion = usePreferredReducedMotion();
    const shouldReduceMotion = computed(() => preferredMotion.value === 'reduce');

    onMounted(() => {
        mm.add(
            {
                desktop: '(min-width: 1024px)',
                tablet: '(min-width: 768px)',
                mobile: '(max-width: 767.98px)'
            },
            () => {
                if (shouldReduceMotion.value) {
                    return () => {};
                }

                const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
                tl.from(parallaxGroup.value, { opacity: 0, duration: 0.6 }).to(
                    circleMain.value,
                    {
                        scale: '+=0.03',
                        duration: 0.7,
                        yoyo: true,
                        repeat: 1
                    },
                    '>-0.1'
                );

                const layerRefs = {
                    backLeft: circleBackLeft.value,
                    backRight: circleBackRight.value,
                    farRight: circleFarRight.value,
                    main: circleMain.value
                };

                const onMove = (e: MouseEvent) => {
                    const x = e.clientX - window.innerWidth / 2;
                    const y = e.clientY - window.innerHeight / 2;
                    parallaxLayers.forEach(({ key, damp }, i) => {
                        gsap.to(layerRefs[key], {
                            x: x * damp,
                            y: y * damp,
                            duration: 0.2,
                            ease: 'none',
                            overwrite: 'auto',
                            delay: i * 0.01
                        });
                    });
                };
                window.addEventListener('mousemove', onMove, { passive: true });

                return () => {
                    window.removeEventListener('mousemove', onMove);
                };
            }
        );
    });

    onBeforeUnmount(() => {
        mm.revert();
    });
</script>

<template>
    <div class="background animated">
        <div class="white-gradient"></div>

        <svg
            class="bg-stretch"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="backgroundViewBox"
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <defs>
                <linearGradient
                    id="bg-b"
                    x1="0"
                    x2="0"
                    y1="0"
                    y2="1"
                >
                    <stop
                        offset="0"
                        :stop-color="backgroundColors.baseStart"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.baseEnd"
                        stop-opacity="0"
                    />
                </linearGradient>
            </defs>

            <rect
                class="background-gradient"
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="url(#bg-b)"
            />
        </svg>

        <svg
            class="bg-svg mobile"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="backgroundViewBox"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            <defs>
                <linearGradient
                    id="mobile-main-shape-gradient"
                    x1="0"
                    x2="1"
                    y1="1"
                    y2="0"
                >
                    <stop
                        offset="0"
                        :stop-color="backgroundColors.mainShapeStart"
                        stop-opacity="0.3"
                    />
                    <stop
                        offset="0.65"
                        :stop-color="backgroundColors.mainShapeEnd"
                        stop-opacity="0.85"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.mainShapeEnd"
                    />
                </linearGradient>

                <linearGradient
                    id="mobile-back-right-gradient"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="1"
                >
                    <stop
                        offset="0"
                        :stop-color="backgroundColors.backRightStart"
                    />
                    <stop
                        offset="0.29"
                        :stop-color="backgroundColors.backRightMid"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.backRightEnd"
                    />
                </linearGradient>
                <linearGradient
                    id="mobile-far-right-gradient"
                    x1="0"
                    x2="0"
                    y1="0"
                    y2="1"
                >
                    <stop
                        offset="0"
                        :stop-color="backgroundColors.farRightStart"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.farRightEnd"
                    />
                </linearGradient>
            </defs>

            <g id="mobile-scene">
                <ellipse
                    :cx="mobileCircles.main.cx"
                    :cy="mobileCircles.main.cy"
                    :rx="mobileCircles.main.rx"
                    :ry="mobileCircles.main.ry"
                    fill="url(#mobile-main-shape-gradient)"
                />
                <ellipse
                    :cx="mobileCircles.farRight.cx"
                    :cy="mobileCircles.farRight.cy"
                    :rx="mobileCircles.farRight.rx"
                    :ry="mobileCircles.farRight.ry"
                    fill="url(#mobile-far-right-gradient)"
                />
                <ellipse
                    :cx="mobileCircles.backRight.cx"
                    :cy="mobileCircles.backRight.cy"
                    :rx="mobileCircles.backRight.rx"
                    :ry="mobileCircles.backRight.ry"
                    fill="url(#mobile-back-right-gradient)"
                    opacity="0.9"
                />
                <ellipse
                    :cx="mobileCircles.backLeft.cx"
                    :cy="mobileCircles.backLeft.cy"
                    :rx="mobileCircles.backLeft.rx"
                    :ry="mobileCircles.backLeft.ry"
                    :fill="backgroundColors.highlight"
                    opacity="0.15"
                />
            </g>
        </svg>

        <svg
            class="bg-svg desktop"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="backgroundViewBox"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            <defs>
                <linearGradient
                    id="main-shape-gradient"
                    x1="0"
                    x2="1"
                    y1="1"
                    y2="0"
                >
                    <stop
                        offset="0"
                        :stop-color="backgroundColors.mainShapeStart"
                        stop-opacity="0.3"
                    />
                    <stop
                        offset="0.65"
                        :stop-color="backgroundColors.mainShapeEnd"
                        stop-opacity="0.85"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.mainShapeEnd"
                    />
                </linearGradient>

                <linearGradient
                    id="back-right-gradient"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="1"
                >
                    <stop
                        offset="0"
                        :stop-color="backgroundColors.backRightStart"
                    />
                    <stop
                        offset="0.29"
                        :stop-color="backgroundColors.backRightMid"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.backRightEnd"
                    />
                </linearGradient>
                <linearGradient
                    id="far-right-gradient"
                    x1="0"
                    x2="0"
                    y1="0"
                    y2="1"
                >
                    <stop
                        offset="0"
                        :stop-color="backgroundColors.farRightStart"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.farRightEnd"
                    />
                </linearGradient>
            </defs>

            <g
                id="scene"
                ref="parallaxGroup"
            >
                <ellipse
                    ref="circleMain"
                    class="circle-main"
                    :cx="circles.main.cx"
                    :cy="circles.main.cy"
                    :rx="circles.main.rx"
                    :ry="circles.main.ry"
                    fill="url(#main-shape-gradient)"
                />
                <ellipse
                    ref="circleFarRight"
                    class="circle-quaternary"
                    :cx="circles.farRight.cx"
                    :cy="circles.farRight.cy"
                    :rx="circles.farRight.rx"
                    :ry="circles.farRight.ry"
                    fill="url(#far-right-gradient)"
                />
                <ellipse
                    ref="circleBackRight"
                    class="circle-secondary"
                    :cx="circles.backRight.cx"
                    :cy="circles.backRight.cy"
                    :rx="circles.backRight.rx"
                    :ry="circles.backRight.ry"
                    fill="url(#back-right-gradient)"
                    opacity="0.9"
                />
                <ellipse
                    ref="circleBackLeft"
                    class="circle-tertiary"
                    :cx="circles.backLeft.cx"
                    :cy="circles.backLeft.cy"
                    :rx="circles.backLeft.rx"
                    :ry="circles.backLeft.ry"
                    :fill="backgroundColors.highlight"
                    opacity="0.15"
                />
            </g>
        </svg>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/urology/urology-hero-background-animated.scss';
</style>
